import React, { forwardRef } from "react";

const NAVY = "#002E5A";
const SECTION = "#004E7C";
const GREEN = "#2E5A44";
const CYAN = "#00E8F0";
const DARK = "#1A1A1A";
const NETO = "#1E402B";
const BORDER = "#CCCCCC";
const WHITE = "#ffffff";

const fmt = (value, { money = false, emptyZero = false } = {}) => {
  const n = Number(value) || 0;
  if (emptyZero && !(n > 0)) return "";
  const text = n.toLocaleString("es-VE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return money ? `$${text}` : text;
};

const cell = {
  border: `1px solid ${BORDER}`,
  padding: "5px 7px",
  fontSize: 12,
  color: DARK,
  verticalAlign: "middle",
};

const headCell = {
  ...cell,
  background: NAVY,
  color: WHITE,
  fontWeight: 700,
  textAlign: "center",
  border: "1px solid #001c38",
};

const sectionCell = {
  background: SECTION,
  color: WHITE,
  fontWeight: 700,
  fontSize: 13,
  padding: "7px 10px",
  textAlign: "left",
};

const highlight = {
  ...cell,
  background: CYAN,
  fontWeight: 700,
  color: DARK,
};

const MilkReceiptSheet = forwardRef(function MilkReceiptSheet({ model }, ref) {
  if (!model) return null;

  const payments = model.payments?.length ? model.payments : [{ date: "", usd: 0, rate: 0, bs: 0 }];

  return (
    <div
      ref={ref}
      style={{
        width: 860,
        background: WHITE,
        color: DARK,
        fontFamily: "Calibri, Arial, Helvetica, sans-serif",
        boxSizing: "border-box",
        padding: 12,
      }}
    >
      <div
        style={{
          background: NAVY,
          color: WHITE,
          textAlign: "center",
          padding: "10px 12px 4px",
          fontSize: 20,
          fontWeight: 700,
        }}
      >
        {model.companyName}
      </div>
      <div
        style={{
          background: NAVY,
          color: WHITE,
          textAlign: "center",
          padding: "0 12px 10px",
          fontSize: 13,
        }}
      >
        {model.companyRif}
      </div>

      <div
        style={{
          color: GREEN,
          fontSize: 18,
          fontWeight: 700,
          padding: "12px 4px 10px",
        }}
      >
        RECIBO DE PAGO DE LECHE
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 10 }}>
        <tbody>
          <tr>
            <td style={{ ...cell, width: "22%", fontWeight: 700 }}>Productor / Cliente:</td>
            <td style={{ ...cell, width: "38%", fontWeight: 700 }}>{model.providerName}</td>
            <td style={{ ...cell, width: "18%", fontWeight: 700, textAlign: "right" }}>Semana N°:</td>
            <td style={{ ...cell, width: "22%", textAlign: "center" }}>{model.weekNo}</td>
          </tr>
          <tr>
            <td style={{ ...cell, fontWeight: 700 }}>Fecha de Emisión:</td>
            <td style={cell}>{model.emitDate}</td>
            <td style={{ ...cell, fontWeight: 700, textAlign: "right" }}>Tipo de Leche:</td>
            <td style={{ ...cell, textAlign: "center", fontWeight: 700 }}>{model.milkType}</td>
          </tr>
        </tbody>
      </table>

      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 12 }}>
        <thead>
          <tr>
            <th colSpan={5} style={sectionCell}>
              1. REGISTRO DIARIO DE RECOLECCIÓN (LITROS)
            </th>
          </tr>
          <tr>
            <th style={{ ...headCell, width: "22%" }}>Día</th>
            <th style={{ ...headCell, width: "18%" }}>Litros Vaca</th>
            <th style={{ ...headCell, width: "18%" }}>Litros Búfala</th>
            <th style={{ ...headCell, width: "18%" }}>Total Diario (L)</th>
            <th style={headCell}>Observación</th>
          </tr>
        </thead>
        <tbody>
          {model.dayRows.map((row) => (
            <tr key={row.name}>
              <td style={cell}>{row.name}</td>
              <td style={{ ...cell, textAlign: "center" }}>{fmt(row.vaca, { emptyZero: true })}</td>
              <td style={{ ...cell, textAlign: "center" }}>{fmt(row.bufalo, { emptyZero: true })}</td>
              <td style={{ ...cell, textAlign: "center" }}>{fmt(row.total)}</td>
              <td style={cell}>{row.obs}</td>
            </tr>
          ))}
          <tr>
            <td style={highlight}>TOTAL LITROS SEMANA</td>
            <td style={{ ...highlight, textAlign: "center" }}>{fmt(model.litersVaca)}</td>
            <td style={{ ...highlight, textAlign: "center" }}>{fmt(model.litersBufalo)}</td>
            <td style={{ ...highlight, textAlign: "center" }}>{fmt(model.litersTotal)}</td>
            <td style={highlight} />
          </tr>
        </tbody>
      </table>

      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th colSpan={5} style={sectionCell}>
              2. LIQUIDACIÓN FINANCIERA
            </th>
          </tr>
          <tr>
            <th style={{ ...headCell, width: "28%" }}>Concepto</th>
            <th style={{ ...headCell, width: "16%" }}>Litros / Cant.</th>
            <th style={{ ...headCell, width: "16%" }}>Precio ($/L)</th>
            <th style={{ ...headCell, width: "18%" }}>Monto ($)</th>
            <th style={headCell}>Monto (Bs.)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={cell}>Leche de Vaca</td>
            <td style={{ ...cell, textAlign: "center" }}>{fmt(model.litersVaca)}</td>
            <td style={{ ...cell, textAlign: "center" }}>{fmt(model.priceCow, { money: true })}</td>
            <td style={{ ...cell, textAlign: "center" }}>{fmt(model.owedVaca, { money: true })}</td>
            <td style={{ ...cell, textAlign: "center" }} />
          </tr>
          <tr>
            <td style={cell}>Leche de Búfala</td>
            <td style={{ ...cell, textAlign: "center" }}>{fmt(model.litersBufalo)}</td>
            <td style={{ ...cell, textAlign: "center" }}>{fmt(model.priceBuffalo, { money: true })}</td>
            <td style={{ ...cell, textAlign: "center" }}>{fmt(model.owedBufalo, { money: true })}</td>
            <td style={{ ...cell, textAlign: "center" }} />
          </tr>
          <tr>
            <td style={{ ...cell, fontWeight: 700 }}>Subtotal Bruto</td>
            <td style={cell} />
            <td style={cell} />
            <td style={{ ...cell, textAlign: "center", fontWeight: 700 }}>
              {fmt(model.subtotal, { money: true })}
            </td>
            <td style={cell} />
          </tr>
          <tr>
            <td style={cell}>(-) Descuentos / Flete</td>
            <td style={cell} />
            <td style={cell} />
            <td style={{ ...cell, textAlign: "center" }}>{fmt(model.discountUsd, { money: true })}</td>
            <td style={{ ...cell, textAlign: "center" }}>
              {fmt(model.discountBs, { emptyZero: true })}
            </td>
          </tr>
          <tr>
            <td style={{ ...headCell, textAlign: "left" }}>Abonos</td>
            <td style={headCell}>Fecha</td>
            <td style={headCell}>Monto</td>
            <td style={headCell}>Tasa</td>
            <td style={headCell}>Monto (Bs.)</td>
          </tr>
          {payments.map((pay, index) => (
            <tr key={`pay-${index}`}>
              <td style={cell} />
              <td style={{ ...cell, textAlign: "center" }}>{pay.date}</td>
              <td style={{ ...cell, textAlign: "center" }}>{fmt(pay.usd, { money: true, emptyZero: true })}</td>
              <td style={{ ...cell, textAlign: "center" }}>{fmt(pay.rate, { emptyZero: true })}</td>
              <td style={{ ...cell, textAlign: "center" }}>{fmt(pay.bs, { emptyZero: true })}</td>
            </tr>
          ))}
          <tr>
            <td style={cell}>(+) Adicionales / Bonos</td>
            <td style={cell} />
            <td style={cell} />
            <td style={{ ...cell, textAlign: "center" }}>{fmt(model.adicionales, { money: true })}</td>
            <td style={{ ...cell, textAlign: "center" }}>
              {fmt(model.adicionalesBs, { emptyZero: true })}
            </td>
          </tr>
          <tr>
            <td style={{ ...highlight, color: NETO }}>TOTAL NETO ($)</td>
            <td style={highlight} />
            <td style={highlight} />
            <td style={{ ...highlight, textAlign: "center", color: NETO }}>
              {fmt(model.neto, { money: true })}
            </td>
            <td style={highlight} />
          </tr>
          <tr>
            <td style={{ ...highlight, color: NETO, fontSize: 14 }}>TOTAL A COBRAR (BS.)</td>
            <td style={highlight} />
            <td style={highlight} />
            <td style={highlight} />
            <td style={{ ...highlight, textAlign: "center", color: NETO, fontSize: 14 }}>
              {fmt(model.totalBs)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
});

export default MilkReceiptSheet;
